import { cartAtom } from "@/atoms"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"
import { getMerchantId } from "@/lib/company-storage"
import { indexCartLines, type CartLineIndex } from "@/lib/scheduling/cart-lines"
import {
  fetchScheduleSlots,
  scheduleSlotsParams,
  type ScheduleSlotsAddress,
} from "@/lib/scheduling/fetch-schedule-slots"
import { tenantClock } from "@/lib/scheduling/normalize-hours"
import type { ScheduleSlots } from "@/lib/scheduling/schedule-slots"
import type { ServiceType } from "@/types"
import { useAtomValue } from "jotai"
import { useEffect, useMemo, useRef, useState } from "react"

/** Widest horizon the contract allows (`max_days_ahead ≤ 30`, D4). */
const MAX_DAYS_AHEAD = 30

export type ScheduleSlotsState = {
  /** False while the first read for the current query is in flight. */
  known: boolean
  loading: boolean
  slots: ScheduleSlots | null
  /** True when the read failed; the caller offers the standard mode instead. */
  failed: boolean
  lines: CartLineIndex
}

type UseScheduleSlotsParams = {
  enabled: boolean
  serviceType: ServiceType | undefined
  address: ScheduleSlotsAddress | null
}

function horizonBounds(timezone: string | null): { from: string; to: string } {
  const now = new Date()
  const from = tenantClock(now, timezone).date
  const to = tenantClock(new Date(now.getTime() + MAX_DAYS_AHEAD * 86_400_000), timezone).date
  return { from, to }
}

/**
 * The offered grid for the current bag, channel and address (§9.3).
 *
 * Only ever runs with `featureFlags.futureWindows` on and a channel chosen —
 * and on DELIVERY only once an address is picked, because the grid is anchored
 * on its ETA (D3). Changing the address changes the query key, which refetches
 * and drops the previous answer.
 */
export function useScheduleSlots({
  enabled,
  serviceType,
  address,
}: UseScheduleSlotsParams): ScheduleSlotsState {
  const cart = useAtomValue(cartAtom)
  const { futureWindows, timezone } = useStorefrontScheduling()
  const [state, setState] = useState<{ slots: ScheduleSlots | null; failed: boolean; key: string }>({
    slots: null,
    failed: false,
    key: "",
  })
  const inFlight = useRef<AbortController | null>(null)

  const lines = useMemo(() => indexCartLines(cart), [cart])

  const query = useMemo(() => {
    if (!enabled || !futureWindows || !serviceType) return null
    if (serviceType === "DELIVERY" && !address) return null
    const merchantId = getMerchantId()
    if (!merchantId) return null

    const { from, to } = horizonBounds(timezone)
    return {
      merchantId,
      serviceType,
      from,
      to,
      itemOfferIds: lines.itemOfferIds,
      optionIds: lines.optionIds,
      address,
    }
  }, [enabled, futureWindows, serviceType, address, timezone, lines])

  const key = query ? `${query.merchantId}?${scheduleSlotsParams(query)}` : ""
  const pending = useRef(query)
  pending.current = query

  useEffect(() => {
    inFlight.current?.abort()
    const current = pending.current
    if (!current) {
      setState({ slots: null, failed: false, key: "" })
      return
    }

    const controller = new AbortController()
    inFlight.current = controller

    void (async () => {
      try {
        const slots = await fetchScheduleSlots(current, controller.signal)
        if (controller.signal.aborted) return
        setState({ slots, failed: false, key })
      } catch {
        if (controller.signal.aborted) return
        setState({ slots: null, failed: true, key })
      }
    })()

    return () => controller.abort()
  }, [key])

  return {
    known: key !== "" && state.key === key,
    loading: key !== "" && state.key !== key,
    slots: state.key === key ? state.slots : null,
    failed: state.key === key && state.failed,
    lines,
  }
}
